import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Tier } from '@mondaygpt/constants'
import { Package } from '@revenuecat/purchases-js'
import { useCallback, useEffect, useState } from 'react'
import { useAnalytics } from '../../../hooks'
import { selectBusinessId } from '../../common/selectors/authentication-view.selector'
import { useAppDispatch, useAppSelector } from '../../core'
import { fetchBusinessBilling } from '../slices/credit-balance.slice'
import { PaywallSource, purchasePackage, selectRevenueCat } from '../slices'
import { getCheckoutProperties } from '../utils/checkout-properties'
import type { BillingPeriod } from './PlanPicker'
import { PaywallView } from './PaywallView'

interface PaywallProps {
  isOpen: boolean
  onClose: () => void
  source?: NonNullable<PaywallSource>
  page?: string
  allowClose?: boolean
}

export const Paywall = ({ isOpen, onClose, source, page, allowClose = true }: PaywallProps) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { track } = useAnalytics()

  const {
    currentOffering,
    error: revenueCatError,
    isConfigured,
    isLoading,
  } = useAppSelector(selectRevenueCat)
  const dispatch = useAppDispatch()
  const businessId = useAppSelector(selectBusinessId)

  useEffect(() => {
    if (isOpen && isConfigured) {
      track(AnalyticsEvents.Paywall.PAYWALL_VIEWED, {
        surface: 'in_app',
        source,
        page,
      })
    }
  }, [isOpen, isConfigured, track, source, page])

  const handlePurchasePackage = useCallback(
    async (pkg: Package, tier: Tier, period: BillingPeriod) => {
      setIsProcessing(true)
      setErrorMessage(null)

      const checkoutProps = getCheckoutProperties(pkg, tier, period)

      try {
        track(AnalyticsEvents.Paywall.CHECKOUT_STARTED, {
          surface: 'in_app',
          source,
          ...checkoutProps,
        })

        const result = await dispatch(purchasePackage({ packageToPurchase: pkg }))

        if (result.meta.requestStatus === 'rejected') {
          if (result.payload === '__cancelled__') return
          throw new Error((result.payload as string) || 'Purchase failed')
        }

        if (businessId) {
          dispatch(fetchBusinessBilling(businessId))
        }

        track(AnalyticsEvents.Paywall.CHECKOUT_COMPLETED, {
          surface: 'in_app',
          source,
          ...checkoutProps,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Purchase failed'
        setErrorMessage(message)

        console.error('[Paywall] checkout failed:', { source, tier, period, error })

        track(AnalyticsEvents.Paywall.CHECKOUT_FAILED, {
          surface: 'in_app',
          source,
          plan_name: checkoutProps.plan_name,
          billing_period: checkoutProps.billing_period,
          error_message: message,
        })
        throw error
      } finally {
        setIsProcessing(false)
      }
    },
    [dispatch, track, source, businessId]
  )

  const handleClose = () => {
    track(AnalyticsEvents.Paywall.PAYWALL_DISMISSED, { surface: 'in_app', source })
    onClose()
  }

  return (
    <PaywallView
      isOpen={isOpen}
      isLoading={isLoading}
      isProcessing={isProcessing}
      isConfigured={isConfigured}
      errorMessage={errorMessage}
      revenueCatError={revenueCatError}
      source={source}
      onClose={handleClose}
      offering={currentOffering}
      onPurchasePackage={handlePurchasePackage}
      allowClose={allowClose}
    />
  )
}
